// ability-button.ts — client-side port of generate_ability_button() +
// determine_ability_button_input_id() (includes/functions/janus/core/ability_button_renderer.php).
//
// The action button on every spell / menta / ability / weapon row. Emits the
// `.btn_ability_ranges` button with all the data-* attrs the existing template.js
// click handlers consume (handleAbilityBtnSingleClick / handleuseAbilityInput1… /
// handleuseAbilityVariable1… / handleuseAbilityVariable2… ; `.use-ability-range` for
// AOE). Reused by the spell/menta tabs and charge-hint hydration of PHP buttons.
//
// Reads through the ambient ft* readers (ftLayerValue/ftLayerEntries/ftItemsCol). The
// rune face reuses the rune-icon bundle: we emit the same .ft-rune-mount markup
// ft_rune_icon_mount_html() does and let window.ftMountRuneIcons() hydrate it after inject.
//
// Charge availability is a red-ring HINT (abilityChargeShortReason below) — an own charge pool at 0, or a
// consumes_ability_charges pool (e.g. a Spellblade's Aether) below the cost, paints the same
// "can't use" ring as the AP/mana checks. The browser's cast gate decides use.

import type { CachedCaster } from './types';
import { readInventoryEquippedToken } from '../janus-engine/compile/inventory-raw-readers';
import { findCarriedInventoryEntry } from '../janus-engine/inventory/find-inventory-entry';
import { isDedicatedCastSanguine } from './sanguine-dedicated-cast';
import { sanguineDmgPickEligible, sanguineDmgSelectId } from './sanguine-damage-type-pick';
import { aoeExcludesCaster } from '../shared/aoe-caster-exclusion';
import { layerListEntryUuid, spellComponentRefusal, statusIds, type SpellComponentReaders } from '../janus-engine/actions/spell-components';
import { casterStatusChoiceIds } from '../janus-engine/ability/statuses/caster-status-choice';
import { encounterInCombat, PSI_MECHANIC_ID } from '../janus-engine/economy/out-of-combat';
import { displayCasterFromCache, fetchDisplayCaster } from './display-caster-cache';
import { manaReductionDelta, manaInvestmentBudget, chargeReductionDelta, chargeInvestmentBudget, type ReductionReaders } from '../janus-engine/resources/reduction-guard';

interface CasterLike extends CachedCaster {
    triggers?: unknown[];
}

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

// StatusEffect::getId(variableName:'concentrating') (includes/catalogs/StatusEffect.php).
// A creature carries this status while concentrating on a spell/ability. Used purely as a
// UX hint: a concentration-requiring button is amber-ringed when the caster already holds it,
// because using another concentration effect would drop the current one. The button stays
// fully clickable — the browser cast handles the swap.
const CONCENTRATING_STATUS_ID = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';

type AbilityButtonWindow = Record<string, unknown>;

function abtnCall(name: string, ...args: unknown[]): unknown {
    const fn = (window as unknown as AbilityButtonWindow)[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

function abtnRunSnippet(snippet: string): void {
    if (!snippet) return;
    // Snippets are generated by this module from fixed call templates; this preserves
    // the existing trigger contract without embedding executable HTML attributes.
    Function(snippet)();
}

// World-placement item types — map rows, never a caster's carried gear. Twin of NON_CARRYABLE_ITEM_TYPES
// (tabs/action-bar.ts) and FT_CASTER_NON_CARRYABLE_ITEM_TYPES (get_weapon_for_attack_ability.php).
const ABTN_WORLD_OBJECT_TYPES = new Set<string>([
    'furniture', 'door', 'building', 'window', 'wall', 'roof', 'floor',
    'vehicle_land', 'vehicle_water', 'vehicle_air', 'war_machine', 'ore_vein', 'tree', 'trap', 'digsite',
]);

/** The world-object types an ability's item-type gate names (Ram the Gate → door / building / war_machine /
 *  vehicle_land), or [] for a carryable-imbue gate, an area ability, or no gate. */
function abtnWorldObjectGateTypes(blob: unknown): string[] {
    if (!blob || !ftLayerHas(blob, 'ability_works_only_against_item_type')) return [];
    if (ftLayerValue(blob, 'aoe_type')) return [];
    const entries = (window as unknown as { ftLayerEntries?: (arr: unknown, layer: string) => unknown[] })
        .ftLayerEntries?.(blob, 'ability_works_only_against_item_type') ?? [];
    const types = [...new Set(entries.map((entry) => String(entry ?? '').trim().toLowerCase()).filter(Boolean))];
    return types.length > 0 && types.every((type) => ABTN_WORLD_OBJECT_TYPES.has(type)) ? types : [];
}

/** An object-only ability (Ram the Gate) is excluded from creature targeting and from the own-item picker, so its
 *  button used to fire with NO target and Janus answered "requires a target" (staging smoke 2026-09-15). Project the
 *  object the player selected in the scene (my_campaign.php records it) as this cast's explicit target.
 *  Returns false when the cast must not proceed. */
export function abtnProjectWorldObjectTarget(abilityId: string): boolean {
    const w = window as unknown as {
        ftFindFullNestedItem?: (id: string) => unknown;
        ftFt3dSelectedObject?: { id?: string; itemType?: string; operable?: boolean; label?: string; encounterId?: string } | null;
        ftSetTargetSelection?: (value: string, reason?: string) => void;
        ftGetActiveEncounterId?: () => string;
        displayWarning?: (message: string) => void;
    };
    const types = abtnWorldObjectGateTypes(abilityId ? w.ftFindFullNestedItem?.(abilityId) : null);
    if (types.length === 0) return true;                       // not a world-object ability: ordinary dispatch
    const readable = types.map((type) => type.replace(/_/g, ' ')).join(', ');
    const selected = w.ftFt3dSelectedObject;
    const targetId = String(selected?.id ?? '').trim();
    const targetType = String(selected?.itemType ?? '').toLowerCase();
    const sceneEncounter = String(w.ftGetActiveEncounterId?.() ?? '').toUpperCase();
    const pickedIn = String(selected?.encounterId ?? '').toUpperCase();
    // Fail CLOSED on every uncertainty (Codex review 2026-09-15): no pick, an unknown type, a pick made in
    // another encounter, or no way to project the target — warn and swallow the click rather than casting blind.
    if (!targetId || !targetType) {
        w.displayWarning?.(`Select the ${readable} you want to target in the scene first.`);
        return false;
    }
    if (!types.includes(targetType) && !(selected?.operable === true && types.includes('war_machine'))) {
        w.displayWarning?.(`${selected?.label || 'That object'} is not a valid target — this action works on: ${readable}.`);
        return false;
    }
    if (sceneEncounter && pickedIn && sceneEncounter !== pickedIn) {
        w.displayWarning?.(`Select the ${readable} you want to target in this encounter first.`);
        return false;
    }
    if (typeof w.ftSetTargetSelection !== 'function') {
        w.displayWarning?.('Targeting is unavailable — reload the scene and try again.');
        return false;
    }
    w.ftSetTargetSelection(targetId, 'world-object-ability');
    return true;
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-abtn-action]')
        : null;
    if (!el) return;
    const action = el.dataset.ftAbtnAction || '';
    if (action === 'run-snippet') {
        event.preventDefault();
        if (!abtnProjectWorldObjectTarget(el.getAttribute('data-ability_id') || '')) return;
        abtnRunSnippet(el.dataset.ftUseSnippet || '');
        return;
    }
    if (action === 'trigger-primary') {
        event.preventDefault();
        abtnCall('ftTriggeredAbilityDebouncedPrimaryClick', event, el);
        return;
    }
    if (action === 'toggle-trigger') {
        event.preventDefault();
        abtnCall(
            'toggleTriggerActive',
            el.dataset.ftAbilityId || '',
            el.dataset.ftEncounterCreatureId || '',
            el.dataset.ftEncounterId || '',
            el.dataset.ftInactive === '1' ? 1 : 0,
        );
    }
});

function casterIsConcentrating(caster: CasterLike): boolean {
    const statuses = ftCreatureStatuses(caster, 'target');
    return Array.isArray(statuses) && statuses.some((st) => ftItemsCol(st, 'item_id') === CONCENTRATING_STATUS_ID);
}

// ── charge-style availability hints ──────────────────────────────────────────
// Spell lock, the ability's own pool and referenced pools share the disabled style.
// Nested weapon/inventory charge-rule layers are not walked. HINT only.
import { abilityEntriesOfType, abilityEntryId, abilityEntryCharges, abilityEntryMaxCharges, abilityEntryName, resolveAbilityNest, uuidOf, chargeInput } from './tabs/ability-row';
import { spellManaDiscount } from '../janus-engine/ability/caster-spell-layers';
import { spellLockReason } from '../janus-engine/actions/spell-lock';
import type { AbilityPreconditionReaders } from '../janus-engine/actions/ability-preconditions';
import { num } from './read-helpers';
import { decodeHtmlEntitiesForPlainText } from './../shared/decode-html-entities';

// AbilityType::LIST (includes/additions.php) — the buckets a charge-pool ability can live in.
const CHARGE_ABILITY_TYPES = [
    'passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability',
    'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'menta',
    'sanguine', 'ability_choice',
];

/** The caster's LIVE entry for an ability id (compiled charges.current rides on it), across
 *  every ability bucket — twin of the PHP layer_entries($caster_arr, $type) scan. */
function findCasterAbilityEntry(caster: CasterLike, abilityId: string): unknown {
    if (!abilityId) return null;
    for (const t of CHARGE_ABILITY_TYPES) {
        for (const e of abilityEntriesOfType(caster, t)) {
            if (abilityEntryId(e).toUpperCase() === abilityId.toUpperCase()) return e;
        }
    }
    return null;
}

/** Wizard v9 spell_mana_discount mirror (janus-engine/ability/caster-spell-layers.ts — the same provider walk and circle scope):
 *  the summed in-scope discount on this SPELL's mana cost. 0 when none, or when the sheet cannot prove a provider (HINT only — the
 *  engine's cast gate decides). */
export function sheetSpellManaDiscount(caster: CasterLike, ability: unknown): number {
    const spell = recordOf(ability);
    if (!spell) return 0;
    try {
        const reads = {
            value: (row: unknown, key: string) => ftLayerValue(row, key),
            has: (row: unknown, key: string) => ftLayerHas(row, key),
            entries: (row: unknown, key: string) => ftLayerEntries(row, key),
            statuses: (row: unknown) => (ftCreaturePayloadStatuses(row) ?? []).map(recordOf).filter((s): s is Record<string, unknown> => s !== null),
            inCombat: () => false,
            resolveItem: (entry: unknown) => recordOf(resolveAbilityNest(entry, caster as unknown as CachedCaster)),
        } as unknown as AbilityPreconditionReaders;
        return spellManaDiscount(reads, caster as unknown as Record<string, unknown>, spell) ?? 0;
    } catch {
        return 0;
    }
}

/** Sanguine Quickened/Urgent mirror: the caster's lowest armed next_spell_ap_override, or null.
 *  Mirrors ft_sanguine_next_spell_ap_override() — ZERO is meaningful (Urgent = free cast);
 *  negative/garbage values are inert. Spell-only + slow-cast gates live at the call site. */
function casterNextSpellApOverride(caster: CasterLike): number | null {
    const statuses = ftCreaturePayloadStatuses(caster);
    if (!Array.isArray(statuses)) return null;
    let override: number | null = null;
    for (const st of statuses) {
        const status = recordOf(st);
        const def = recordOf(status?.status_arr) ?? st;
        if (!ftLayerHas(def, 'next_spell_ap_override')) continue;
        const raw = lv(def, 'next_spell_ap_override');
        const n = Number(Array.isArray(raw) ? raw[0] : raw);
        if (!Number.isFinite(n) || n < 0) continue;
        override = override === null ? n : Math.min(override, n);
    }
    return override;
}

const SLOW_CASTING_TIMES = ['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours'];

function sheetReductionReaders(caster: CasterLike): ReductionReaders {
    return { value: (row, key) => ftLayerValue(row, key), entries: (row, key) => ftLayerEntries(row, key),
        statuses: row => (ftCreaturePayloadStatuses(row) ?? []).map(recordOf).filter((s): s is Record<string, unknown> => s !== null),
        resolveItem: entry => recordOf(resolveAbilityNest(entry, caster as unknown as CachedCaster)) };
}

export function sheetChargeCost(caster: CasterLike, cost: number, item?: unknown): number {
    return -chargeReductionDelta(caster as unknown as Record<string, unknown>, -cost, sheetReductionReaders(caster), recordOf(item));
}

/** A protected empty pool still offers the investment the creature's full mana capacity normally supports. */
export function sheetManaBudget(caster: CasterLike): number {
    const current = Math.max(0, Number(ftEncounterCreatureField(caster.encounter_creatures, 'mana') ?? 0));
    return manaInvestmentBudget(caster as unknown as Record<string, unknown>, current, sheetReductionReaders(caster));
}

/** How many charges this creature may invest from a held pool entry: the shared chargeInvestmentBudget rule read off the entry's
 *  charges cluster (protected: higher of current and maximum; otherwise current). Every stepper, "can't use" ring and Psi count
 *  on the sheet and action bar reads THIS, never the raw pool — the one sheet-side site of that rule. */
export function sheetChargeBudget(caster: CasterLike, entry: unknown): number {
    const current = Number(abilityEntryCharges(entry, 0)), max = Number(abilityEntryMaxCharges(entry, 0));
    const budget = chargeInvestmentBudget(caster as unknown as Record<string, unknown>, Number.isFinite(current) ? current : 0,
        Number.isFinite(max) ? max : 0, sheetReductionReaders(caster));
    return Math.max(0, Math.trunc(budget));
}

/** Wizard v9 next_summon_ap_override mirror (janus-engine/ability/summon.ts summonApOverrideStatus — Architect of Emergence): the caster's
 *  lowest armed override for its next SUMMON cast, or null. A HINT only — the engine's cast gate decides. */
const SUMMON_LAYER_KEYS = ['summon', 'user_choice_1_summon', 'user_choice_2_summon', 'user_choice_3_summon', 'user_choice_4_summon'];
function casterNextSummonApOverride(caster: CasterLike, ability: unknown): number | null {
    if (!SUMMON_LAYER_KEYS.some(key => ftLayerHas(ability, key))) return null;
    const statuses = ftCreaturePayloadStatuses(caster);
    if (!Array.isArray(statuses)) return null;
    let override: number | null = null;
    for (const st of statuses) {
        const status = recordOf(st);
        const def = recordOf(status?.status_arr) ?? st;
        if (!ftLayerHas(def, 'next_summon_ap_override')) continue;
        const raw = lv(def, 'next_summon_ap_override');
        const n = Number(Array.isArray(raw) ? raw[0] : raw);
        if (!Number.isFinite(n) || n < 0) continue;
        override = override === null ? Math.trunc(n) : Math.min(override, Math.trunc(n));
    }
    return override;
}

/** ability_charges_not_required status opt-out: a held status naming the pool waives the spend. */
function chargeSpendWaivedByStatus(caster: CasterLike, abilityId: string): boolean {
    const statuses = ftCreaturePayloadStatuses(caster);
    if (!Array.isArray(statuses)) return false;
    const want = abilityId.toUpperCase();
    for (const st of statuses) {
        const status = recordOf(st);
        const def = recordOf(status?.status_arr) ?? st;
        for (const e of ftLayerEntries(def, 'ability_charges_not_required')) {
            if (uuidOf(e).toUpperCase() === want) return true;
        }
    }
    return false;
}

/** A variable-target spend: max_targets AND consumes_charges_qty both [x] (Perfect Pitch: X Melody = X targets). The Action
 *  Bar picks X on its targeting stepper AFTER the click, starting at 1, so the click gate and the bar's red ring ask for 1 —
 *  not ability_charge_qty_for_x_placeholder's no-input spell-level default, which still governs a cast that posts no X. */
export function barPicksVariableTargetCharges(ability: unknown): boolean {
    return ftLayerValue(ability, 'consumes_charges_qty') === '[x]'
        && determineAbilityButtonInputFamily(ability) === 'variable-targets';
}

/** Read combat on the held encounter, including a lean omitted zero. */
function casterCombatState(caster: CasterLike): boolean | null {
    const id = String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_id') ?? '').toUpperCase();
    return encounterInCombat(recordOf(window.FateTide?.encounterCache?.get(id))
        ?? recordOf(caster.encounter_arr) ?? recordOf(caster.encounter_context));
}

/** '' when usable; else a short human reason for the red ring + tooltip. */
function abilityChargeShortReason(ability: unknown, caster: CasterLike, barChoosesAmount = false, selectedAmount?: unknown): string {
    if (sheetChargeCost(caster, 1) === 0) return '';
    // 1. OWN charge pool exhausted. The pool spends on this use when the caster is in combat OR
    //    the ability has no turn-recharge range (creature_ability_charge_pool_spends_on_this_use).
    if (ftLayerHas(ability, 'ability_charges')) {
        const inCombat = casterCombatState(caster) === true;
        const hasTurnRecharge = ftLayerHas(ability, 'recharge_range_begin') && ftLayerHas(ability, 'recharge_range_end');
        if (inCombat || !hasTurnRecharge) {
            const own = findCasterAbilityEntry(caster, String(ftItemsCol(ability, 'item_id') ?? ''));
            const cur = own ? abilityEntryCharges(own, 'NA') : 'NA';
            if (cur !== 'NA' && cur !== '' && cur !== null && cur !== undefined && num(cur, 0) <= 0) {
                return 'no charges left';
            }
        }
    }
    // 2. consumes_ability_charges — the referenced pool (Aether / Mastery / …) must cover the cost.
    for (const ref of ftLayerEntries(ability, 'consumes_ability_charges')) {
        const refId = uuidOf(ref);
        if (!refId) continue;
        // Use the held encounter, not combat membership: even a non-member's Psi
        // cost is unchanged while its encounter is in combat.
        if (refId.toUpperCase() === PSI_MECHANIC_ID) {
            if (casterCombatState(caster) === false) continue;
        }
        if (chargeSpendWaivedByStatus(caster, refId)) continue;
        const entry = findCasterAbilityEntry(caster, refId);
        if (!entry) continue; // an unheld pool cannot prove a shortage
        const qty = ftLayerValue(ability, 'consumes_charges_qty');
        let required = 1;
        if (qty === '[x]') {
            // ability_charge_qty_for_x_placeholder with no input: menta/spell level, else 1
            // (the [x] input's minimum spend — can't use the ability at all below that).
            const lvl = num(ftLayerValue(ability, 'menta_level') ?? ftLayerValue(ability, 'spell_level'), 0);
            const chosen = num(selectedAmount, 0);
            required = chosen > 0 ? Math.trunc(chosen)
                : lvl > 0 && !(barChoosesAmount && barPicksVariableTargetCharges(ability)) ? lvl : 1;
        } else if (qty === '[max]') {
            required = num(abilityEntryMaxCharges(entry, 0), 0);
        } else if (qty === '[any]') {
            continue; // spends whatever is there — never blocks (PHP parity)
        } else if (qty !== null && qty !== undefined && qty !== '') {
            required = num(qty, 1);
        }
        if (num(abilityEntryCharges(entry, 0), 0) < required) {
            const nm = abilityEntryName(entry);
            return (nm && nm !== 'Unknown') ? `not enough ${nm} charges` : 'not enough ability charges';
        }
    }
    return '';
}

function buttonChargeShortReason(ability: unknown, caster: CasterLike, spellType: string, barChoosesAmount = false, selectedAmount?: unknown): string {
    return spellLockReason(caster, ability, ftLayerHas)
        || abilityChargeShortReason(ability, caster, barChoosesAmount, selectedAmount)
        || (spellType === 'innate_spell_per_long_rest' ? innatePoolShortReason(caster, String(ftItemsCol(ability, 'item_id') ?? '')) : '');
}

// ── required caster status (a Monk stance) ───────────────────────────────────
// The browser cast gate's REQUIRED-STATUS rule (ability-preconditions.ts): an ability carrying
// if_caster_has_status_effect_active refuses unless the caster holds ANY listed status. HINT only — the
// tile greys and names what it needs; the click still reaches the gate. Hide / Hunker are judged by the
// scene there, and a vitality tier is not a live row, so neither greys here.
const SCENE_JUDGED_STATUS_GATES = new Set(['C7326C89-CD32-C8C8-2DA9-29D66FFC2284', '8C28EBE3-60EA-D493-0FB8-67859CE92F63']);
const VITALITY_STATUS_IDS = new Set(['82788F70-EBEF-44C4-80E7-6622AD20DD60', 'FF30B76E-785D-2CAF-FC66-C131D0548808', '658483AA-34DE-74E5-C974-E3E202C31CC7',
    'C97076EF-FED9-19B4-D109-C22F9BC740E8', '44B8792E-632E-2651-C666-67DFD181E92A', '298BE1DD-2917-476E-D79C-92CF8C598926',
    'D5DE14C4-3194-87BC-D767-6D45BDD90552', '8AE129AF-52D8-CCE8-706A-85767DB52740', '9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA']);

function requiredStatusName(id: string, entry: unknown): string {
    let definition: unknown = null;
    try { definition = window.ftFindFullNestedItem?.(id) ?? null; } catch { definition = null; }
    const name = ftItemsCol(definition, 'item_name') ?? ftItemsCol(entry, 'item_name') ?? window.FT_ICON_REGISTRY?.[id]?.tooltip;
    return typeof name === 'string' ? decodeHtmlEntitiesForPlainText(name) : '';
}

/** '' unless an in-encounter caster holds none of the statuses this ability requires; else "requires Shu Stance". */
export function requiredCasterStatusReason(ability: unknown, caster: CasterLike): string {
    if (!ftEncounterCreatureField(caster.encounter_creatures, 'encounter_id')) return '';
    const entries = ftLayerEntries(ability, 'if_caster_has_status_effect_active') ?? [];
    if (entries.length === 0 || SCENE_JUDGED_STATUS_GATES.has(String(ftItemsCol(ability, 'item_id') ?? '').toUpperCase())) return '';
    const required = entries.map((entry: unknown) => ({ id: layerListEntryUuid(entry).toUpperCase(), entry })).filter(({ id }) => id !== '');
    if (required.length === 0 || required.some(({ id }) => VITALITY_STATUS_IDS.has(id))) return '';
    const held = statusIds(caster as unknown as Record<string, unknown>, spellComponentReaders);
    if (required.some(({ id }) => held.has(id))) return '';
    const names = [...new Set(required.map(({ id, entry }) => requiredStatusName(id, entry)).filter(name => name !== ''))];
    return names.length > 0 ? `requires ${names.join(' or ')}` : 'requires a status you do not have active';
}

function abilityResourceHint(ability: unknown, caster: CasterLike, apRequired: string | number, spellType: string | null, castFreeOfMana: boolean) {
    const itemType = String(ftItemsCol(ability, 'item_type') ?? '');
    const casterAps = ftEncounterCreatureField(caster.encounter_creatures, 'aps') ?? 0;
    const casterMana = ftEncounterCreatureField(caster.encounter_creatures, 'mana') ?? 0;
    const concentrationBusy = ftLayerHas(ability, 'spell_concentration') && casterIsConcentrating(caster);

    // AP-affordability hint: when this costs more AP than the caster has left → the SAME red "can't use" ring
    // as concentration-busy (owner 2026-06-26). HINT only — clicking still reaches the browser
    // cast gate (no AP/charge spent on refusal). Gated on the ENCOUNTER context (encounter_id), NOT strict
    // in_combat — the AP pips show then too, and a creature can hold AP before initiative flips in_combat, so
    // the ring must match the pips or it silently never appears. Skipped for non-numeric / 0-AP costs.
    const apCostNum = typeof apRequired === 'number' ? apRequired : parseInt(String(apRequired), 10);
    const inEncounter = !!ftEncounterCreatureField(caster.encounter_creatures, 'encounter_id');
    const apsRemaining = Number(Array.isArray(casterAps) ? (casterAps[0] ?? 0) : casterAps) || 0;
    const apShort = inEncounter && Number.isFinite(apCostNum) && apCostNum > 0 && apsRemaining < apCostNum;
    const inCombat = casterCombatState(caster) === true;
    const reactionShort = inCombat && apRequired === 'r' && !ftLayerValue(ability, 'class_aura')
        && Number(ftEncounterCreatureField(caster.encounter_creatures, 'reaction') ?? 0) < 1;
    // Spell mana cost == spell_level (use_ability_pre_conditions L614). Skip the mana-FREE casts: at-will is
    // never deactivated, innate-per-rest + charged casts pay CHARGES (the object's), not the pool. caller
    // passes castFreeOfMana for a bound spell whose type it looked up; spellType covers the fly-up path.
    const manaFreeCast = castFreeOfMana || spellType === 'at_will_spell' || spellType === 'innate_spell_per_long_rest'
        || spellType === 'charged_spell' || spellType === 'charged_ability';
    const spellLvl = (itemType === 'spell' || itemType === 'curse') ? (Number(lv(ability, 'spell_level')) || 0) : 0;
    const manaRemaining = Number(Array.isArray(casterMana) ? (casterMana[0] ?? 0) : casterMana) || 0;
    // Wizard v9 spell_mana_discount: the cost the cast actually pays (floor 0).
    const manaCost = spellLvl > 0 && itemType === 'spell' ? Math.max(0, spellLvl - sheetSpellManaDiscount(caster, ability)) : spellLvl;
    const manaShort = inEncounter && !manaFreeCast && spellLvl > 0
        && manaRemaining < -manaReductionDelta(caster as unknown as Record<string, unknown>, -manaCost, sheetReductionReaders(caster));
    // requires_in/out_of_combat are static layers (use_ability_pre_conditions L122-138). in_combat unwrapped.
    const reqOutShort = ftLayerHas(ability, 'requires_out_of_combat') && inCombat;
    const reqInShort = ftLayerHas(ability, 'requires_in_combat') && inEncounter && !inCombat;
    // ONE shared red "can't use" ring for any cheap-to-check pre-condition (class kept as -ap-short to reuse
    // the CSS). HINT only — clickable → browser cast gate. Concentration-busy wins (no double-ring).
    const statusFlag = apRequired === 'r' ? 'no_reactions' : 'no_actions';
    const statusBlocked = !itemType.endsWith('_skill') && (ftCreaturePayloadStatuses(caster) || []).some((entry) => {
        const status = recordOf(entry);
        if (!status || Number(status.status_private ?? 0) !== 0 || statusEffectInstanceIsCampaignPaused(status)) return false;
        return Number(ftLayerValue(recordOf(status.status_arr) ?? status, statusFlag) ?? 0) !== 0;
    });
    // Spell components (check_spell_components): verbal while silenced, somatic with no free hand / restrained and no
    // bypass (ignore flag / No Fingers Required, incl. the Combat Casting feat), a consumed material not carried.
    // At-will / innate / charged casts skip components (spellType rides as variable_1; castFreeOfMana
    // marks a bound free cast). NOT gated on inEncounter — the browser gate runs out of combat too.
    const componentShortReason = spellComponentShortReason(ability, caster, spellType, castFreeOfMana);
    const statusRequiredReason = requiredCasterStatusReason(ability, caster);
    const otherResourceShort = statusBlocked || apShort || reactionShort || manaShort || reqOutShort || reqInShort || componentShortReason !== '';
    const priorityReason = spellLockReason(caster, ability, ftLayerHas) || (statusBlocked ? (statusFlag === 'no_reactions' ? 'an active status prevents reactions' : 'an active status prevents actions')
        : apShort ? 'not enough AP'
        : reactionShort ? 'not enough reactions'
        : manaShort ? 'not enough mana'
            : reqOutShort ? 'only usable out of combat'
                : reqInShort ? 'only usable in combat' : '');
    return { concentrationBusy, otherResourceShort, priorityReason, componentShortReason, statusRequiredReason };
}

export interface ActionBarChargePill {
    remaining: number;
    maximum: number;
    empty: boolean;
    label: string;
}

/** Presentation of the held pool, including the exact inventory instance paying an item cast. */
export function actionBarChargePillModel(ability: unknown, caster: CasterLike, spellType = '', source = ''): ActionBarChargePill | null {
    const id = abilityEntryId(ability);
    if (id.toUpperCase() === '4744CC19-87A9-41D7-9285-ABD36470EC81') return null; // Short Rest's glyph owns its count.
    let current: unknown;
    let max: unknown;
    let sourceName = '';
    if (spellType === 'charged_spell' || spellType === 'charged_ability') {
        if (!source) return null;
        const inventory = findCarriedInventoryEntry(caster, source, { entries: ftLayerEntries, inventoryField: ftInventoryField });
        if (!inventory) return null;
        current = ftLayerValue(inventory, 'current_item_charges');
        max = ftLayerValue(inventory, 'item_charges');
        sourceName = decodeHtmlEntitiesForPlainText(String(ftInventoryField(inventory, 'inventory_name') || ftItemsCol(inventory, 'item_name') || 'Item'));
    } else {
        const entry = findCasterAbilityEntry(caster, id)
            ?? abilityEntriesOfType(caster, 'innate_spell_per_long_rest').find(row => abilityEntryId(row).toUpperCase() === id.toUpperCase())
            ?? ability;
        max = abilityEntryMaxCharges(entry, 0);
        current = abilityEntryCharges(entry, 0);
    }
    const maximum = Number(max);
    const remaining = Math.max(0, Math.trunc(Number(current)));
    if (!Number.isFinite(maximum) || maximum <= 0 || !Number.isFinite(remaining)) return null;
    return { remaining, maximum, empty: remaining === 0,
        label: `${remaining} of ${maximum} charges left${sourceName ? ` — ${sourceName}` : ''}` };
}

export function renderActionBarChargePill(pill: ActionBarChargePill | null): string {
    return pill ? `<span class="ft-action-charge-pill${pill.empty ? ' ft-action-charge-pill--empty' : ''}" role="img" aria-label="${escapeHtml(pill.label)}" title="${escapeHtml(pill.label)}">${pill.remaining}</span>` : '';
}

/** Keep both button and pill identity on charge deltas; unchanged pools do not mutate the DOM. */
export function syncActionBarChargePill(button: HTMLButtonElement, model: ActionBarChargePill | null): void {
    const pill = button.querySelector<HTMLElement>('.ft-action-charge-pill');
    if (!model) { pill?.remove(); return; }
    if (!pill) { button.insertAdjacentHTML('beforeend', renderActionBarChargePill(model)); return; }
    if (pill.getAttribute('aria-label') === model.label) return;
    if (pill.textContent !== String(model.remaining)) pill.textContent = String(model.remaining);
    pill.classList.toggle('ft-action-charge-pill--empty', model.empty);
    pill.setAttribute('aria-label', model.label);
    pill.setAttribute('title', model.label);
}

const chargeHintDefinitions = new WeakMap<HTMLButtonElement, unknown>();
type ChargeHintQueue = { buttons: Set<HTMLButtonElement>; pending: boolean };
const chargeHintQueue = (window as Window & { _ftChargeHintQueue?: ChargeHintQueue })._ftChargeHintQueue
    ??= { buttons: new Set<HTMLButtonElement>(), pending: false };

/** Charge hints retain handlers and pointer events for the browser cast gate and press-hold info. */
export function hydrateAbilityChargeHints(root: ParentNode = document, casterId?: string): void {
    const selector = 'button[data-ft-charge-hint="1"]';
    const buttons = [...root.querySelectorAll<HTMLButtonElement>(selector)];
    if (typeof Element !== 'undefined' && root instanceof Element && root.matches(selector)) buttons.unshift(root as HTMLButtonElement);
    for (const button of buttons) {
        const id = button.dataset.ftCasterId ?? '';
        if (id && (!casterId || id.toUpperCase() === casterId.toUpperCase())) chargeHintQueue.buttons.add(button);
    }
    if (chargeHintQueue.pending || chargeHintQueue.buttons.size === 0) return;
    chargeHintQueue.pending = true;
    queueMicrotask(() => {
        chargeHintQueue.pending = false;
        const buttons = [...chargeHintQueue.buttons]; chargeHintQueue.buttons.clear();
        refreshAbilityChargeHints(buttons);
    });
}

function refreshAbilityChargeHints(buttons: HTMLButtonElement[]): void {
    for (const button of buttons) {
        if (!button.isConnected || button.disabled || button.closest('.tab-pane:not(.active), [hidden]')
            || button.checkVisibility?.({ checkVisibilityCSS: true }) === false) continue;
        const id = button.dataset.ftCasterId ?? '';
        const caster = displayCasterFromCache(id) ?? displayCasterFromCache(id.toUpperCase());
        if (!caster) {
            // Same coalesced cold-cache reader as the sheet panes. A genuinely unavailable
            // caster has its existing named refusal; this never requests a PHP rule decision.
            if (button.dataset.ftChargeLoading !== '1') {
                button.dataset.ftChargeLoading = '1';
                void fetchDisplayCaster(id).then(() => {
                    if (button.isConnected) hydrateAbilityChargeHints(button);
                }).catch(() => {}).finally(() => { delete button.dataset.ftChargeLoading; });
            }
            continue;
        }
        const abilityId = button.getAttribute('data-ability_id') ?? '';
        let ability = chargeHintDefinitions.get(button);
        if (!ability) {
            // Read the same definition the renderer received, before rune hydration drops
            // its script. Only this small definition is retained; the caster stays in cache.
            const json = button.querySelector('.ft-rune-blob')?.textContent;
            if (json) { try { ability = JSON.parse(json); } catch { /* rune hydration owns malformed JSON */ } }
            if (!recordOf(ability)) ability = resolveAbilityNest(abilityId, caster);
            if (recordOf(ability)) chargeHintDefinitions.set(button, ability);
        }
        if (!recordOf(ability)) continue;
        const inputId = button.getAttribute('data-input_id_reference') ?? '';
        const input = inputId ? document.getElementById(inputId) as HTMLInputElement | null : null;
        const barChoosesAmount = button.dataset.ftChargeBarChoosesAmount === '1';
        const spellType = button.getAttribute('data-spell_type') || null;
        const { concentrationBusy, otherResourceShort, priorityReason, componentShortReason, statusRequiredReason } = abilityResourceHint(
            ability, caster, button.getAttribute('data-ap_required') ?? '', spellType, button.dataset.ftFreeMana === '1',
        );
        const reason = buttonChargeShortReason(ability, caster, button.getAttribute('data-spell_type') ?? '', barChoosesAmount,
            !barChoosesAmount && button.dataset.ftInputFamily === 'variable-charges' ? input?.value : undefined);
        const short = reason !== '';
        const name = decodeHtmlEntitiesForPlainText(String(ftItemsCol(ability, 'item_name') ?? 'Ability'));
        const baseReason = priorityReason || componentShortReason;
        const baseTitle = statusRequiredReason ? `${name} — ${statusRequiredReason}`
            : concentrationBusy ? `${name} — you're already concentrating` : baseReason ? `${name} — ${baseReason}` : name;
        button.dataset.ftChargeBaseTitle = baseTitle;
        button.dataset.ftChargeBaseRing = otherResourceShort && !concentrationBusy ? '1' : '0';
        button.dataset.ftChargePriorityTitle = statusRequiredReason || concentrationBusy || priorityReason ? '1' : '0';
        button.classList.toggle('ft-ability-btn-status-gated', statusRequiredReason !== '');
        button.classList.toggle('ft-ability-btn-concentration-busy', concentrationBusy);
        button.classList.toggle('ft-ability-btn-charge-blocked', short);
        button.classList.toggle('ft-ability-btn-ap-short', !button.classList.contains('ft-ability-btn-concentration-busy')
            && (short || button.dataset.ftChargeBaseRing === '1'));
        if (!button.classList.contains('ft-ability-btn-rune')) {
            button.classList.toggle('btn-warning', short);
            button.classList.toggle('btn-info', !short);
        }
        if (short) {
            button.dataset.ftChargeBlocked = '1';
            button.setAttribute('aria-disabled', 'true');
            button.setAttribute('tabindex', '-1');
        } else {
            delete button.dataset.ftChargeBlocked;
            button.removeAttribute('aria-disabled');
            button.removeAttribute('tabindex');
        }
        const showChargePill = button.dataset.ftShowChargePill === '1';
        const pill = showChargePill ? actionBarChargePillModel(ability, caster, spellType ?? '', button.getAttribute('data-source') ?? '') : null;
        if (showChargePill) syncActionBarChargePill(button, pill);
        const title = (short && button.dataset.ftChargePriorityTitle !== '1' ? `${name} — ${reason}` : baseTitle)
            + (pill ? ` — ${pill.label}` : '');
        if (showChargePill && button.getAttribute('aria-label') !== title) button.setAttribute('aria-label', title);
        for (const field of ['aps', 'reaction', 'mana']) button.setAttribute(`data-caster_${field}`,
            String(ftEncounterCreatureField(caster.encounter_creatures, field) ?? 0));
        button.setAttribute('data-caster_in_combat', casterCombatState(caster) ? '1' : '0');
        button.setAttribute('data-bs-toggle', 'tooltip');
        const titleChanged = button.getAttribute('data-bs-original-title') !== title;
        if (titleChanged) button.setAttribute('data-bs-original-title', title);
        button.removeAttribute('title');
        // An existing Bootstrap instance otherwise keeps its first tooltip after replenishment.
        const tooltip = (window as unknown as { bootstrap?: { Tooltip?: { getInstance(el: Element): { setContent(content: Record<string, string>): void } | null } } }).bootstrap?.Tooltip?.getInstance(button);
        if (titleChanged) tooltip?.setContent({ '.tooltip-inner': title });
    }
}

function mountAbilityChargeHints(): void {
    hydrateAbilityChargeHints();
    if (typeof MutationObserver === 'undefined') return;
    new MutationObserver(records => {
        for (const record of records) for (const node of record.addedNodes) {
            if (node instanceof Element) hydrateAbilityChargeHints(node);
        }
    }).observe(document.body, { childList: true, subtree: true });
}

// Aggregates may also carry this renderer. One page-owned hydrator keeps their
// observers and cold reads shared without importing another bundle's cache module.
if (typeof (window as unknown as AbilityButtonWindow).ftHydrateAbilityChargeHints !== 'function') {
    Object.assign(window, { ftHydrateAbilityChargeHints: hydrateAbilityChargeHints });
    if (typeof window.addEventListener === 'function') {
        window.addEventListener('ft:caster-state-applied', (event: Event) => {
            const id = (event as CustomEvent<{ casterId?: string }>).detail?.casterId;
            if (id) hydrateAbilityChargeHints(document, id);
        });
        for (const event of ['ft:encounter-state-received', 'ft:encounter-turn-state']) {
            window.addEventListener(event, () => hydrateAbilityChargeHints());
        }
    }
    for (const event of ['input', 'change']) document.addEventListener(event, (event) => {
        if (typeof HTMLInputElement !== 'undefined' && event.target instanceof HTMLInputElement && event.target.id) hydrateAbilityChargeHints();
    });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAbilityChargeHints);
    else if (typeof Element !== 'undefined') queueMicrotask(mountAbilityChargeHints);
}

/** Innate per-long-rest pool (twin of the ability-preconditions.ts INNATE POOL gate): '' when a use is left or the
 *  sheet cannot prove the count; else the refusal reason. Reads the caster's innate bucket entry's compiled charges
 *  cluster, then the pre-baked computed.creature.castable payload — a pool with neither is FULL, never assumed empty. */
function innatePoolShortReason(caster: CasterLike, abilityId: string): string {
    if (sheetChargeCost(caster, 1) === 0) return '';
    const want = abilityId.toUpperCase();
    if (want === '') return '';
    const idOf = (row: unknown) => String(recordOf(row)?.item_id ?? '').toUpperCase();
    const bucket = ftLayerEntries(caster, 'innate_spell_per_long_rest');
    const bakedRaw = (caster.computed?.creature as { castable?: { innate_spell_per_long_rest?: unknown } } | undefined)?.castable?.innate_spell_per_long_rest;
    const baked: unknown[] = Array.isArray(bakedRaw) ? bakedRaw : [];
    // The pool belongs to the spell itself, else (ability_choice branch) to the held innate PARENT whose
    // ability_choice list names it — the gate's parentId rule.
    let poolId = want;
    if (!bucket.some(row => idOf(row) === want) && !baked.some(row => idOf(row) === want)) {
        const parent = bucket.find((row) => {
            const def = resolveAbilityNest(row, caster as unknown as CachedCaster);
            return ftLayerEntries(def, 'ability_choice').some((child) => {
                const c = recordOf(child);
                return String(c ? (c.ability_id ?? c.item_id ?? '') : (child ?? '')).toUpperCase() === want;
            });
        });
        if (!parent) return '';
        poolId = idOf(parent);
    }
    const matches = (row: unknown) => idOf(row) === poolId;
    let current: unknown = recordOf(recordOf(bucket.find(matches))?.charges)?.current;
    if (current === undefined) current = recordOf(recordOf(baked.find(matches))?.charges)?.current;
    if (current === undefined || current === null || current === '' || !Number.isFinite(Number(current))) return '';
    return Math.trunc(Number(current)) < 1 ? 'no uses left — recharges on a long rest' : '';
}

/** template.js hook for the castable-spells ability_choice row: its child cast button arrives as server HTML
 *  (query_ability_choice_inc.php), so paint the same innate-pool "can't use" ring onto it client-side. */
export function ftMarkInnatePoolShort(button: Element | null, casterId: string, abilityId: string, spellType: string): void {
    if (!button || spellType !== 'innate_spell_per_long_rest' || button.classList.contains('ft-ability-btn-concentration-busy')) return;
    const cache = window.FateTide?.casterCache;
    const want = String(casterId || '').toUpperCase();
    // The row carries the caster's request id (encounter_creature_id, else item_id). Strongest identity first: the
    // cache key, then a row's encounter_creature_id, and only then an item_id (shared by every placed copy of the item).
    const entries = cache ? [...cache.entries()] : [];
    const caster = (entries.find(([k]) => String(k).toUpperCase() === want)
        ?? entries.find(([, row]) => String(recordOf(recordOf(row)?.encounter_creatures)?.encounter_creature_id ?? '').toUpperCase() === want)
        ?? entries.find(([, row]) => String(ftItemsCol(row, 'item_id') ?? '').toUpperCase() === want))?.[1] as CasterLike | undefined;
    if (!caster) return;
    const reason = innatePoolShortReason(caster, abilityId);
    if (reason === '' || button.classList.contains('ft-ability-btn-ap-short')) return;
    button.classList.add('ft-ability-btn-ap-short');
    const name = button.getAttribute('title') || 'Ability';
    button.setAttribute('data-ft-charge-base-title', name);
    button.setAttribute('title', `${name} — ${reason}`);
}
Object.assign(window, { ftMarkInnatePoolShort });

// ── spell-component precheck ─────────────────────────────────────────────────
// check_spell_components() via the shared pure mirror (janus-engine/actions/spell-components.ts — the same code the FT3D
// placement gate refuses with). HINT only: rings what the cached row PROVES the server refuses (silenced, restrained without an
// ignore bypass, both hands held without a No Fingers Required / ignore bypass, a consumed material not carried); anything
// unreadable never rings.
const spellComponentReaders: SpellComponentReaders = {
    has: (row, key) => ftLayerHas(row, key),
    entries: (row, key) => ftLayerEntries(row, key) ?? [],
    statuses: (row) => (ftCreatureStatuses(row, 'target') ?? []).map((st: unknown) => recordOf(st)).filter((st): st is Record<string, unknown> => st !== null),
    resolveItem: (entry) => {
        const id = abilityEntryId(entry);
        if (!id) return null;
        try { return recordOf(window.ftFindFullNestedItem?.(id)); } catch { return null; }
    },
};

/** '' when every component gate passes (or cannot be proven to fail); else the short hover reason. */
function spellComponentShortReason(ability: unknown, caster: CasterLike, spellType: string | null, castFreeOfComponents: boolean): string {
    const abilityRow = recordOf(ability);
    if (!abilityRow || castFreeOfComponents) return '';
    return spellComponentRefusal(abilityRow, caster as unknown as Record<string, unknown>, spellType, null, spellComponentReaders)?.shortReason ?? '';
}

// CastingTime::IDS (includes/additions.php) — casting_time → AP.
const CASTING_TIME_AP: Record<string, number | string> = {
    '3ap': 3, '2ap': 2, '1ap': 1, reaction: 'r',
    one_minute: 3, ten_minutes: 3, one_hour: 3, eight_hours: 3, twelve_hours: 3, twenty_four_hours: 3,
};
function castingTimeAp(ct: string): number | string {
    return CASTING_TIME_AP[ct] ?? 3;
}

// get_ability_action_points(item_type) (includes/functions.php).
// AP costs MUST match the browser ability gate (ability-preconditions.ts), which ported check_caster_aps_and_in_combat() (retired R-025 S7 — docs/archive/r025-retired-php-helpers-2026-09-17.md):
// attack_ability / weapon / natural_weapon cost 1 AP — without these cases they fell to
// default:3 and the affordability ring + "not enough AP" tooltip wrongly painted on every
// attack button whenever the caster had <3 AP (they only need 1).
function abilityActionPoints(itemType: string): number | string {
    switch (itemType) {
        case 'full_ability': case 'Full Ability': return 3;
        case 'ability': case 'Ability': return 2;
        case 'quick_ability': case 'Quick Ability':
        case 'attack_ability': case 'Attack Ability':
        case 'lair_ability': case 'Lair Ability':
        case 'weapon': case 'Weapon':
        case 'natural_weapon': case 'Natural Weapon': return 1;
        case 'free_ability': case 'Free Ability':
        case 'sanguine': case 'Sanguine': return 0; // [0ap] by design — matches the server gate
        case 'reaction_ability': case 'Reaction Ability':
        case 'opportunity_attack_ability': case 'Opportunity Attack Ability': return 'r';
        default: return 3;
    }
}

/** Exact client projection of the AP the server charges for one ability use. Shared with FT3D's
 * Reach approach preview so its total is the ability/weapon AP plus routed movement AP. */
export function abilityActionPointCost(ability: unknown, caster: CachedCaster): number | string {
    const itemType = String(ftItemsCol(ability, 'item_type') ?? '');
    let cost: number | string;
    if (itemType === 'spell' || itemType === 'curse' || itemType === 'menta') {
        cost = castingTimeAp(String(lv(ability, 'spell_casting_time') || '3ap'));
    } else {
        cost = abilityActionPoints(itemType);
    }
    if (String(caster?.encounter_creatures?.item_type ?? ftItemsCol(caster, 'item_type') ?? '').toLowerCase() === 'lair') {
        cost = typeof cost === 'number' ? Math.min(cost, 1) : (cost === 'r' ? cost : 1);
    }
    if (itemType === 'spell' && typeof cost === 'number') {
        const castingTime = String(lv(ability, 'spell_casting_time') || '');
        if (!SLOW_CASTING_TIMES.includes(castingTime)) {
            const override = casterNextSpellApOverride(caster);
            if (override !== null) cost = Math.min(cost, override);
        }
    }
    if (typeof cost === 'number') {
        const summonOverride = casterNextSummonApOverride(caster, ability);
        if (summonOverride !== null) cost = Math.min(cost, summonOverride);
    }
    return cost;
}

// determine_spell_casting_icon / determine_menta_casting_icon.
function spellCastingIcon(ct: string): string {
    switch (ct) {
        case '1ap': return 'cast_1ap_spell';
        case '2ap': return 'cast_2ap_spell';
        case 'reaction': return 'cast_reaction_spell';
        default: return 'cast_3ap_spell';
    }
}
function mentaCastingIcon(ct: string): string {
    switch (ct) {
        case '1ap': return 'use_1ap_menta';
        case '2ap': return 'use_2ap_menta';
        case 'reaction': return 'use_reaction_menta';
        default: return 'use_3ap_menta';
    }
}

// ft_rune_icon_eligible(): spell + the AbilityType family (minus ability_choice).
const ABILITY_RUNE_TYPES = new Set<string>([
    'spell', 'curse', 'menta', 'sanguine',
    'free_ability', 'quick_ability', 'attack_ability', 'ability', 'full_ability',
    'opportunity_attack_ability', 'reaction_ability', 'passive_ability', 'lair_ability',
]);
function runeEligible(itemType: string): boolean {
    return itemType !== '' && itemType !== 'ability_choice' && ABILITY_RUNE_TYPES.has(itemType);
}

/** Mirror of ft_rune_icon_mount_html(): a .ft-rune-mount carrying {items,item_layer_types} JSON.
 *  Exported for the action bar's weapon fold-out, whose entries are rune medallions with their OWN
 *  pinned-weapon onclick (generateAbilityButton's auto-resolve fire path would lose the weapon). */
export function runeMountHtml(ability: unknown, size = 72, extraClass = 'ft-rune-ability-btn'): string {
    if (!runeEligible(String(ftItemsCol(ability, 'item_type') ?? ''))) return '';
    const abilityRecord = recordOf(ability);
    const payload = {
        items: abilityRecord?.items ?? {},
        item_layer_types: abilityRecord?.item_layer_types ?? {},
        // Ship the ability's OWN catalog so the rune engine resolves its bare-UUID reference layers
        // (charge/contest refs) to item_name — incl. cross-class refs (e.g. a Wizard "Mastery" charge)
        // that the caster root catalog omits. Engine merges this with the root catalog. Icons are
        // name-keyed (mastery.png etc.), so an unresolved UUID 404s.
        catalog: abilityRecord?.catalog ?? {},
    };
    let json = 'null';
    try { json = JSON.stringify(payload); } catch { json = 'null'; }
    json = json.replace(/<\//g, '<\\/');
    const cls = `ft-rune-mount ${extraClass}`.trim();
    return `<div class="${escapeHtml(cls)}" data-rune-size="${size}"><script type="application/json" class="ft-rune-blob">${json}</script></div>`;
}

// ability_arr_has_triggers(): any 'triggers_' key (excluding the two roll-type ones).
// The hydrated ability nest keeps layers in item_layer_types, so check both buckets.
const TRIGGER_EXCLUDED = new Set(['triggers_on_next_roll_type', 'triggers_on_next_roll_type_against']);
function abilityHasTriggers(ability: unknown): boolean {
    const scan = (value: unknown): boolean => {
        const obj = recordOf(value);
        if (!obj) return false;
        for (const k of Object.keys(obj)) {
            if (k.startsWith('triggers_') && !TRIGGER_EXCLUDED.has(k)) return true;
        }
        return false;
    };
    return scan(ability) || scan(recordOf(ability)?.item_layer_types);
}

// ability_trigger_is_active(): default active; a triggers row with trigger_inactive=1 suppresses.
function abilityTriggerIsActive(abilityId: string, caster: CasterLike): boolean {
    const rows = Array.isArray(caster?.triggers) ? caster.triggers : [];
    for (const row of rows) {
        if (uuidOf(ftLayerValue(row, 'trigger_ability_id')).toUpperCase() === abilityId.toUpperCase()) {
            return Number(ftLayerValue(row, 'trigger_inactive') ?? 0) === 0;
        }
    }
    return true;
}

/** CANTRIP CARVE-OUT — TS twin of ft_spell_is_level_upcastable()
 *  (includes/functions/janus/core/spell_is_level_upcastable.php). A cantrip (spell_level /
 *  menta_level 0) costs no mana, so it is NEVER upcast by picking a level — offering the picker
 *  would hand out its dice_upcast / mod_upcast / duration scaling for free. Cantrips scale
 *  automatically off the caster's total level through spell_scalable_cantrip instead. Authoring
 *  spell_upcastable on a cantrip stays legal (Arcane Muscles does); this predicate is what keeps
 *  the leveled half inert. [[owner 2026-07-24]] */
export function spellIsLevelUpcastable(ability: unknown): boolean {
    if (!ftLayerHas(ability, 'spell_upcastable')) return false;
    const level = ftLayerValue(ability, 'spell_level') ?? ftLayerValue(ability, 'menta_level') ?? 0;
    // Mirror PHP's is_numeric() + (int) cast rather than a bare Number(): a permissive read
    // accepts true / '0x1' / 0.5 where ft_spell_is_leveled() rejects or truncates them, and a
    // twin that drifts on junk data shows a picker the server would then refuse to honour.
    if (typeof level === 'number') return Number.isFinite(level) && Math.trunc(level) > 0;
    if (typeof level !== 'string') return false;
    // is_numeric()'s accepted grammar (PHP 7+ drops hex, so Number()'s '0x1' must not slip in).
    if (!/^\s*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?\s*$/.test(level)) return false;
    return Math.trunc(Number(level)) > 0;
}

/** Port of determine_ability_button_input_id(). */
export function determineAbilityButtonInputId(ability: unknown): string {
    const id = String(ftItemsCol(ability, 'item_id') ?? '');
    const type = String(ftItemsCol(ability, 'item_type') ?? '');

    if (
        ftLayerHas(ability, 'consumes_charges_qty')
        && ftLayerValue(ability, 'consumes_charges_qty') === '[x]'
        && type !== 'spell' && type !== 'menta' && type !== 'curse'
    ) {
        return `input_${id}_variable_charges`;
    }
    if (
        spellIsLevelUpcastable(ability)
        || (ftLayerHas(ability, 'max_targets') && ftLayerValue(ability, 'max_targets') === '[x]')
        || (ftLayerHas(ability, 'consumes_charges_qty') && ftLayerValue(ability, 'consumes_charges_qty') === '[x]')
    ) {
        return `input_${id}_spell_upcastable`;
    }
    const dice = ftLayerValue(ability, 'dice');
    const changeMana = ftLayerValue(ability, 'change_caster_mana');
    if (dice === '[x]' || (changeMana != null && String(changeMana).includes('[x]'))) {
        return `input_${id}_variable_mana_dice`;
    }
    if (ftLayerHas(ability, 'user_choice_1_summon')) return `input_${id}_user_choice_1_summon`;
    if (ftLayerHas(ability, 'user_choice_1_shapeshift')) return `input_${id}_user_choice_1_shapeshift`;
    if (ftLayerHas(ability, 'user_choice_1_essence')) return `input_${id}_user_choice_1_essence`;
    // status_effect_applied_is_caster_choice (Psion Override Emotion, 2026-10-07) — twin of the PHP branch; before damage_type.
    if (casterStatusChoiceIds(statusChoiceReaders, ability as Record<string, unknown>).length) return `input_${id}_user_choice_1_status_effect`;
    if (ftLayerEntries(ability, 'damage_type').length > 1) return `input_${id}_user_choice_1_damage_type`;
    return '';
}

/** Layer readers for casterStatusChoiceIds (the sheet's ftLayer* globals). */
export const statusChoiceReaders = {
    has: (row: Record<string, unknown> | null, key: string): boolean => !!row && ftLayerHas(row, key),
    entries: (row: Record<string, unknown> | null, key: string): unknown[] => (row ? ftLayerEntries(row, key) : []) ?? [],
    value: (row: Record<string, unknown> | null, key: string): unknown => (row ? ftLayerValue(row, key) : null),
};

export type AbilityButtonInputFamily = 'none' | 'spell-level' | 'variable-targets' | 'variable-charges'
    | 'variable-mana' | 'summon' | 'shapeshift' | 'essence' | 'status-effect' | 'damage-type' | 'amount';

/** Semantic twin for the historically overloaded generated input id. Consumers must not infer a
 * cast-level picker from `_spell_upcastable`: that id also carries runtime target and charge counts. */
export function determineAbilityButtonInputFamily(
    ability: unknown,
    inputId = determineAbilityButtonInputId(ability),
): AbilityButtonInputFamily {
    if (!inputId) return 'none';
    if (inputId.endsWith('_variable_charges')) return 'variable-charges';
    if (inputId.endsWith('_spell_upcastable')) {
        if (spellIsLevelUpcastable(ability)) return 'spell-level';
        if (ftLayerValue(ability, 'max_targets') === '[x]') return 'variable-targets';
        if (ftLayerValue(ability, 'consumes_charges_qty') === '[x]') return 'variable-charges';
        return 'amount';
    }
    if (inputId.endsWith('_variable_mana_dice')) return 'variable-mana';
    if (inputId.endsWith('_user_choice_1_summon')) return 'summon';
    if (inputId.endsWith('_user_choice_1_shapeshift')) return 'shapeshift';
    if (inputId.endsWith('_user_choice_1_essence')) return 'essence';
    if (inputId.endsWith('_user_choice_1_status_effect')) return 'status-effect';
    if (inputId.endsWith('_user_choice_1_damage_type')) return 'damage-type';
    return 'amount';
}

const lv = (a: unknown, k: string): string => {
    const v = ftLayerValue(a, k);
    return v === undefined || v === null ? '' : String(v);
};

// Stamp this ability's TARGET-gate spec onto the button as data-* attrs so the targeting bundle can
// amber-ring it when EXACTLY ONE incompatible creature is targeted (see syncAbilityButtonTargetCompatibility
// in targeting-combat.ts). Only emitted when the ability actually carries a target gate — gateless buttons
// stay lean. This is an advisory CLIENT mirror of the server's ability_target_gate_failure(); the server
// stays authoritative (it hard-blocks single-target gate failures before any AP/charge spend). UUIDs are
// upper-cased so DOM-time compares match the server's uppercase ids.
function abilityTargetGateAttrs(ability: unknown, caster: CasterLike): string {
    // Mirror of PHP layer_list_entry_uuid() / item-arr-readers' _ftLayerEntryUuid: entries can be
    // bare strings, {items:{item_id}}, {item_id}, or structured {item_layer_effect, qty/template_id}
    // rows (e.g. migration-written restriction rows) — the last shape stringifies to garbage under
    // a plain String(e) and silently drops the gate attr while the server still enforces it.
    const uuidPrefix = (value: string): string => {
        const delimiter = value.indexOf('|');
        return (delimiter === -1 ? value : value.slice(0, delimiter)).toUpperCase();
    };
    const entryUuid = (e: unknown): string => {
        if (typeof e === 'string') return uuidPrefix(e);
        if (e === null || typeof e !== 'object') return '';
        const bid = ftItemsCol(e, 'item_id');
        if (typeof bid === 'string' && bid !== '') return uuidPrefix(bid);
        const rec = e as Record<string, unknown>;
        if (typeof rec.item_id === 'string' && rec.item_id !== '') return uuidPrefix(rec.item_id);
        if (typeof rec.item_layer_effect === 'string' && rec.item_layer_effect !== '') return uuidPrefix(rec.item_layer_effect);
        return '';
    };
    const list = (k: string): string[] =>
        (ftLayerEntries(ability, k) || [])
            .map(entryUuid)
            .filter((s: string) => checkIfValidUUID(s));

    const szBelow  = lv(ability, 'ability_only_works_against_size_category_number_below');
    const szAbove  = lv(ability, 'ability_only_works_against_size_category_number_above');
    const typeOnly = list('ability_works_only_against_creature_type');
    const typeNot  = list('ability_does_not_work_against_creature_type');
    const subOnly  = list('ability_works_only_against_creature_sub_type');
    const subNot   = list('ability_does_not_work_against_creature_sub_type');
    // 2026-07-15 division restrictions — matched client-side against the same target
    // creature_sub_type set as subOnly/subNot (all divisions attach via that layer), but
    // stamped separately so the preflight warning wording matches the server's.
    const linOnly  = list('ability_works_only_against_creature_lineage');
    const linNot   = list('ability_does_not_work_against_creature_lineage');
    const ancOnly  = list('ability_works_only_against_creature_ancestry');
    const ancNot   = list('ability_does_not_work_against_creature_ancestry');
    const grpOnly  = list('ability_works_only_against_creature_grouping');
    const grpNot   = list('ability_does_not_work_against_creature_grouping');
    const stActive = list('if_target_has_status_effect_active');
    const stNot    = list('if_target_has_status_effect_not_active');

    // Inventory-only abilities (Siege Ram) fail EVERY creature target — stamped as a gate so the
    // single-creature amber ring and click-time preflight refuse it before the endpoint is called.
    const inventoryOnly = lv(ability, 'ability_only_affects_inventory') === '1';

    const hasGate = inventoryOnly || szBelow !== '' || szAbove !== '' || typeOnly.length || typeNot.length
        || subOnly.length || subNot.length || stActive.length || stNot.length
        || linOnly.length || linNot.length || ancOnly.length || ancNot.length
        || grpOnly.length || grpNot.length;
    if (!hasGate) return '';

    const casterSizeRaw = ftLayerValue(caster, 'size');
    const casterSize = casterSizeRaw ? String(casterSizeRaw).toUpperCase() : '';

    const attrs: string[] = ['data-ft-target-gate="1"'];
    if (inventoryOnly)   attrs.push('data-gate_inventory_only="1"');
    if (casterSize)      attrs.push(`data-gate_caster_size="${escapeHtml(casterSize)}"`);
    if (szBelow !== '')  attrs.push(`data-gate_sz_below="${escapeHtml(String(szBelow))}"`);
    if (szAbove !== '')  attrs.push(`data-gate_sz_above="${escapeHtml(String(szAbove))}"`);
    if (typeOnly.length) attrs.push(`data-gate_type_only="${escapeHtml(typeOnly.join(','))}"`);
    if (typeNot.length)  attrs.push(`data-gate_type_not="${escapeHtml(typeNot.join(','))}"`);
    if (subOnly.length)  attrs.push(`data-gate_subtype_only="${escapeHtml(subOnly.join(','))}"`);
    if (subNot.length)   attrs.push(`data-gate_subtype_not="${escapeHtml(subNot.join(','))}"`);
    if (linOnly.length)  attrs.push(`data-gate_lineage_only="${escapeHtml(linOnly.join(','))}"`);
    if (linNot.length)   attrs.push(`data-gate_lineage_not="${escapeHtml(linNot.join(','))}"`);
    if (ancOnly.length)  attrs.push(`data-gate_ancestry_only="${escapeHtml(ancOnly.join(','))}"`);
    if (ancNot.length)   attrs.push(`data-gate_ancestry_not="${escapeHtml(ancNot.join(','))}"`);
    if (grpOnly.length)  attrs.push(`data-gate_grouping_only="${escapeHtml(grpOnly.join(','))}"`);
    if (grpNot.length)   attrs.push(`data-gate_grouping_not="${escapeHtml(grpNot.join(','))}"`);
    if (stActive.length) attrs.push(`data-gate_status_active="${escapeHtml(stActive.join(','))}"`);
    if (stNot.length)    attrs.push(`data-gate_status_not="${escapeHtml(stNot.join(','))}"`);
    return ' ' + attrs.join(' ');
}

export interface AbilityButtonOpts {
    spellType?: string | null;       // e.g. 'at_will_spell', 'charged_spell'
    source?: string | null;          // inventory/enchantment source id
    consumeNestedUnitPerUse?: boolean;
    buttonDisabled?: boolean;
    iconSize?: 's' | 'm' | 'l';      // icon-view rune LOD: s→48 (L0), m→72 (L1, default), l→96 (L2)
    bootstrapTooltip?: boolean;      // icon view: emit a BS tooltip (data-bs-toggle) instead of a native title
    castFreeOfMana?: boolean;        // suppress the mana-short ring (at-will / innate / charged casts don't pay the pool)
    barChoosesAmount?: boolean;      // an Action Bar button: its stepper picks a variable-target [x] spend (barPicksVariableTargetCharges)
    showChargePill?: boolean;        // action-bar slots and flyouts share the live remaining-charge overlay
}

// Icon-view size → rune LOD px (mirrors ftAbilityIconSizeToRunePx in core-helpers.js). Display px is
// CSS-driven (.ft-ai--* sizes .ft-rune-ability-btn); this only picks the sigil detail tier.
function iconSizeToRunePx(size?: 's' | 'm' | 'l'): number {
    return size === 's' ? 48 : (size === 'l' ? 96 : 72);
}

/**
 * Port of generate_ability_button(). Returns the button HTML (wrapped in the trigger
 * sparkle div when the ability has triggers). After injecting, call ftMountRuneIcons
 * on the container to hydrate the rune face.
 */
export function generateAbilityButton(ability: unknown, caster: CasterLike, opts: AbilityButtonOpts = {}): string {
    const { spellType = null, source = null, consumeNestedUnitPerUse = false, buttonDisabled = false, iconSize, bootstrapTooltip = false, castFreeOfMana = false, barChoosesAmount = false } = opts;
    const itemType = String(ftItemsCol(ability, 'item_type') ?? '');

    // 1. AP cost + glyph icon
    let apRequired: number | string;
    let icon: string;
    if (itemType === 'spell' || itemType === 'curse') {
        const ct = lv(ability, 'spell_casting_time') || '3ap';
        apRequired = castingTimeAp(ct);
        icon = iconMap(spellCastingIcon(ct));
    } else if (itemType === 'menta') {
        const ct = lv(ability, 'spell_casting_time') || '3ap';
        apRequired = castingTimeAp(ct);
        icon = iconMap(mentaCastingIcon(ct));
    } else {
        apRequired = abilityActionPoints(itemType);
        icon = iconMap(itemType);
    }

    // A LAIR fires ONE lair action per round (aps_total=1), so ANY of its abilities costs 1 AP —
    // mirrors the server cap in check_caster_aps_and_in_combat.php (item_type==='lair' → min(cost,1)).
    // Without this the affordability ring + "not enough AP" tooltip falsely paint on lair abilities
    // whose declared cost exceeds the lair's single AP (e.g. a composed spell copy).
    if (String(caster?.encounter_creatures?.item_type ?? ftItemsCol(caster, 'item_type') ?? '').toLowerCase() === 'lair') {
        apRequired = typeof apRequired === 'number' ? Math.min(apRequired, 1) : (apRequired === 'r' ? apRequired : 1);
    }

    // Sanguine Quickened/Urgent (next_spell_ap_override): an armed buff caps the next SPELL's AP
    // cost server-side (check_caster_aps_and_in_combat + template_spell_roll) — mirror it here or
    // the affordability ring + "not enough AP" tooltip wrongly paint on the very cast the buff
    // enables (1-AP caster casting a 2ap spell under Quickened). Spells only, numeric only (the
    // 'r' reaction slot is untouched), slow casting times excluded — same gates as PHP.
    if (itemType === 'spell' && typeof apRequired === 'number') {
        const ctForOverride = String(lv(ability, 'spell_casting_time') || '');
        if (!SLOW_CASTING_TIMES.includes(ctForOverride)) {
            const apOverride = casterNextSpellApOverride(caster);
            if (apOverride !== null) apRequired = Math.min(apRequired, apOverride);
        }
    }
    // Wizard v9 next_summon_ap_override (Architect of Emergence): the armed status caps the next SUMMON's AP
    if (typeof apRequired === 'number') {
        const summonApOverride = casterNextSummonApOverride(caster, ability);
        if (summonApOverride !== null) apRequired = Math.min(apRequired, summonApOverride);
    }

    const runeIcon = runeMountHtml(ability, iconSizeToRunePx(iconSize), 'ft-rune-ability-btn');
    const usesRune = runeIcon !== '';
    const buttonInnerIcon = usesRune ? runeIcon : icon;

    // 2. core data
    const abilityId = String(ftItemsCol(ability, 'item_id') ?? '');
    const range = lv(ability, 'range');
    const aoeType = lv(ability, 'aoe_type');
    const inputIdRef = determineAbilityButtonInputId(ability);
    const inputFamily = determineAbilityButtonInputFamily(ability, inputIdRef);

    const casterTeam = uuidOf(ftEncounterCreatureField(caster.encounter_creatures, 'team'));
    const casterAps = ftEncounterCreatureField(caster.encounter_creatures, 'aps') ?? 0;
    const casterReaction = ftEncounterCreatureField(caster.encounter_creatures, 'reaction') ?? 0;
    const casterMana = sheetManaBudget(caster);
    const psiEntry = findCasterAbilityEntry(caster, '6A8183E1-349E-3960-B465-6C863097E193');
    const casterPsi = psiEntry ? sheetChargeBudget(caster, psiEntry) : 0;
    const casterInCombat = casterCombatState(caster) ? 1 : 0;

    // 3. AOE?
    const isUuid = (s: string) => typeof checkIfValidUUID === 'function' && checkIfValidUUID(s);
    const isAoe = range !== '' && isUuid(range) && aoeType !== '' && isUuid(aoeType);

    let dataNestedInvAttr = '';
    if (consumeNestedUnitPerUse && source && isUuid(source)) {
        dataNestedInvAttr = ` data-nested-inventory-id="${escapeHtml(source)}"`;
    }
    // Expose the ability's SOURCE (the weapon/inventory the attack fires from) so the action bar can
    // capture it on drag and replay it on render — otherwise a bound weapon attack loses which weapon
    // it belongs to (draw-sheathed / throw / nested-source variants all key off this).
    const dataSourceAttr = (source && isUuid(source)) ? ` data-source="${escapeHtml(source)}"` : '';

    // 4. use snippet (non-AOE only)
    let useSnippet = '';
    if (!isAoe) {
        useSnippet = `handleAbilityBtnSingleClick('${abilityId}');`;
        if (inputIdRef) useSnippet = `handleuseAbilityInput1BtnSingleClick('${abilityId}', '${inputIdRef}');`;
        // A variable-input ability that ALSO carries a user damage-type choice (Elemental Attack:
        // [x] Aether charges + pick frost/fire/lightning/storm) fires with BOTH inputs — the count
        // rides use_ability_variable_1, the picked type use_ability_variable_2 (damage_type_roll
        // honours a damage_type UUID in either slot). A missing select reads '' → server default.
        const dmgChoiceInputId = `input_${abilityId}_user_choice_1_damage_type`;
        if (inputIdRef && inputIdRef !== dmgChoiceInputId && ftLayerEntries(ability, 'damage_type').length > 1) {
            useSnippet = `handleuseAbilityInput1Input2BtnSingleClick('${abilityId}', '${inputIdRef}', '${dmgChoiceInputId}');`;
        }
        if (spellType) useSnippet = `handleuseAbilityVariable1BtnSingleClick('${abilityId}', '${spellType}');`;
        if (spellType && source) useSnippet = `handleuseAbilityVariable2BtnSingleClick('${abilityId}', '${spellType}', '${source}');`;
        if (lv(ability, 'attack_ability_throws_the_throwing_weapon_at_target')) useSnippet = `handleuseAbilityVariable1BtnSingleClick('${abilityId}', 'throw_weapon');`;
        if (consumeNestedUnitPerUse && source && isUuid(source)) useSnippet = `handleuseAbilityVariable2BtnSingleClick('${abilityId}', 'nested_inventory_source', '${source}');`;
        if (lv(ability, 'draw_sheathed_weapon_for_attack_ability') && isUuid(source ?? '')) useSnippet = `handleuseAbilityVariable2BtnSingleClick('${abilityId}', 'draw_sheathed_weapon', '${source}');`;
        if (lv(ability, 'counterspelling_effect')) useSnippet = `handleuseAbilityVariable1BtnSingleClick('${abilityId}', 'arm_counterspell');`;
        // channel_chosen_orb ("Channel 3 orbs of your choosing"): open the orb picker instead of
        // casting, and let IT fire the cast with the picks on use_ability_variable_2.
        //
        // IT YIELDS TO EVERY OTHER SPECIALIZED MODE, and the gate is deliberately WIDER than
        // "is variable_2 free". Protecting only variable_2 is not enough: a throw / draw /
        // counterspell / marker cast also owns variable_1 and its own dispatch, so replacing its
        // snippet with the picker would silently drop 'arm_counterspell' or 'throw_weapon' even
        // though variable_2 was unspent (Codex review 2026-09-09, confirmed bug #1). Skipping the
        // picker on those abilities costs nothing: ft_resolve_chosen_orb_picks() rolls the
        // authored count server-side, so the orbs are still channeled.
        const chosenOrbCount = Math.floor(Number(lv(ability, 'channel_chosen_orb'))) || 0;
        const chosenOrbYields = (!!spellType && !!source)
            || (consumeNestedUnitPerUse && !!source && isUuid(source))
            || !!lv(ability, 'draw_sheathed_weapon_for_attack_ability')
            || !!lv(ability, 'attack_ability_throws_the_throwing_weapon_at_target')
            || !!lv(ability, 'counterspelling_effect');
        if (chosenOrbCount > 0 && !chosenOrbYields) {
            useSnippet = `ftOrbChoiceCast('${abilityId}', ${chosenOrbCount});`;
        }
        // SANGUINE Elemental/Transmuted: the spell row renders a damage-type select under the canonical
        // id even though this spell authored ≤1 type (castable-spells.ts effectCell). Route the cast so
        // the pick rides use_ability_variable_2 — the server re-authorizes it against the armed carrier.
        // An item-SOURCED cast already spends variable_2 on its source row, so the predicate refuses it.
        // SPECIALIZED modes own both variable slots outright — never let the pick clobber them. Today's
        // catalog can't produce these combinations on a damaging source-less spell (Counterspell rolls no
        // dice, throws/draws are attack abilities), but that's an accident of authoring, not a guarantee.
        // [[Codex review 2026-07-24 P3]]
        const sangSpecializedMode = !!lv(ability, 'counterspelling_effect')
            || !!lv(ability, 'attack_ability_throws_the_throwing_weapon_at_target')
            || !!lv(ability, 'draw_sheathed_weapon_for_attack_ability')
            // An orb PICKER spends variable_2 on the chosen slugs, so it is a specialized mode by
            // the same rule the three above are: the damage-type pick must yield rather than
            // clobber it. Gated on the picker actually winning the snippet — when the orb layer
            // yielded to one of those three, this ability is not in orb-pick mode at all.
            || (chosenOrbCount > 0 && !chosenOrbYields);
        if (!sangSpecializedMode && sanguineDmgPickEligible(caster, ability, !!source)) {
            const sangSelId = sanguineDmgSelectId(abilityId);
            // A marker cast already spends variable_1 on the marker; if the ability ALSO needs a
            // summon/shapeshift choice there is no slot left for it, so leave that snippet alone.
            const sangMarkerNeedsInput1 = !!spellType
                && (inputIdRef.endsWith('_user_choice_1_summon') || inputIdRef.endsWith('_user_choice_1_shapeshift'));
            if (sangMarkerNeedsInput1) {
                // keep the existing marker snippet — the form/creature pick outranks the damage type
            } else if (spellType) {
                // at-will / innate-per-rest: variable_1 keeps the spell type, the pick reads into variable_2.
                useSnippet = `useAbilityVariable1Input1('${abilityId}', '${spellType}', '${sangSelId}');`;
            } else {
                // Normal known/prepared spell: variable_1 stays the upcast level (or '' when the spell has
                // no primary control — the same empty-variable_1 shape the action-bar confirm sends).
                useSnippet = `handleuseAbilityInput1Input2BtnSingleClick('${abilityId}', '${inputIdRef}', '${sangSelId}');`;
            }
        }
        // Dedicated-cast abilities (Ad-lib / Voidgrasping / Contingency sanguines, plus the level-6 SPELL
        // Contingency) do NOT self-buff — their button opens a fold-out form (spell picker +
        // mode/condition) that POSTs the extraordinary-cast / arm endpoint. Overrides any snippet set
        // above. 'spell' is admitted alongside 'sanguine' because Spell Contingency arms the same delayed
        // cast through its own door; isDedicatedCastSanguine() matches an exact id list, so widening the
        // item type here cannot catch an unrelated spell. See sanguine-dedicated-cast.ts.
        if ((itemType === 'sanguine' || itemType === 'spell') && isDedicatedCastSanguine(abilityId)) {
            useSnippet = `ftSanguineDedicatedCast('${abilityId}');`;
        }
    }

    // 5. Base styling; the browser charge hint retains the action handler.
    let btnVariant = 'info';
    if (usesRune) btnVariant = 'dark';
    const runeBtnClass = usesRune ? ' ft-ability-btn-rune' : '';
    let extraClass = isAoe ? ' use-ability-range' : '';
    if (buttonDisabled) { useSnippet = ''; extraClass = ''; }

    const { concentrationBusy, otherResourceShort, priorityReason, componentShortReason, statusRequiredReason } = abilityResourceHint(ability, caster, apRequired, spellType, castFreeOfMana);
    if (statusRequiredReason) extraClass += ' ft-ability-btn-status-gated';
    if (concentrationBusy) extraClass += ' ft-ability-btn-concentration-busy';
    const chargeShortReason = buttonChargeShortReason(ability, caster, spellType ?? '', barChoosesAmount);
    const resourceShort = (otherResourceShort || chargeShortReason !== '') && !concentrationBusy;
    if (resourceShort) extraClass += ' ft-ability-btn-ap-short';
    const chargeShort = !buttonDisabled && chargeShortReason !== '';
    if (chargeShort) {
        extraClass += ' ft-ability-btn-charge-blocked';
        if (!usesRune) btnVariant = 'warning';
    }

    // 7. delegated click attr + trigger snippet
    let actionAttr = useSnippet ? `data-ft-abtn-action="run-snippet" data-ft-use-snippet="${escapeHtml(useSnippet)}"` : '';
    let triggerSnippetAttr = '';
    if (abilityHasTriggers(ability) && useSnippet !== '') {
        triggerSnippetAttr = ` data-ft-trigger-use-snippet="${escapeHtml(useSnippet)}"`;
        actionAttr = 'data-ft-abtn-action="trigger-primary"';
    }
    const disabledAttr = buttonDisabled ? ' disabled' : '';

    // Target-gate spec for the single-target amber warning aura (toggled by the targeting bundle on target change).
    const targetGateAttrs = abilityTargetGateAttrs(ability, caster);

    const bool = (k: string, has = false) => (has ? (ftLayerHas(ability, k) ? '1' : '0') : (lv(ability, k) ? '1' : '0'));
    const excludesAoeCaster = aoeExcludesCaster({
        range,
        shape: ftLayerValue(ability, 'aoe_type'),
        rollType: ftLayerValue(ability, 'roll_type'),
        itemType,
        dice: ftLayerValue(ability, 'dice'),
        die: ftLayerValue(ability, 'die'),
        persistent: ftLayerValue(ability, 'persistent_aoe'),
        followsTarget: ftLayerValue(ability, 'aoe_follows_target'),
    });

    // Name tooltip: ALWAYS use the site's Bootstrap tooltip (data-bs-toggle="tooltip", lazily shown by
    // the delegated mouseover handler in template.js) — matches the icon_map() hover style in BOTH list
    // and icon views (the user asked list view to drop the native browser `title` too). The
    // bootstrapTooltip opt is retained for callers but no longer gates this (both paths are BS now).
    // data-bs-animation="false": the delegated handler force-shows on every mouseover, so a fade would
    // overlap fade-in/out across densely-packed medallions and flicker; no fade = a clean snap.
    void bootstrapTooltip;
    // Encoded in the DB — decode before the escapeHtml on abTitle below, else every ability button's
    // tooltip shows the literal "&#039;". This is the button's own name read (the row/icon callers
    // hand us the ability NEST, not their already-decoded display name).
    const abName = decodeHtmlEntitiesForPlainText(String(ftItemsCol(ability, 'item_name') ?? 'Ability'));
    const shortReason = priorityReason || chargeShortReason || componentShortReason;
    const abTitle = escapeHtml(
        statusRequiredReason ? `${abName} — ${statusRequiredReason}`
            : concentrationBusy ? `${abName} — you're already concentrating`
                : resourceShort ? `${abName} — ${shortReason}`
                    : abName,
    );
    const baseReason = priorityReason || componentShortReason;
    const chargeBaseTitle = statusRequiredReason ? `${abName} — ${statusRequiredReason}`
        : concentrationBusy ? `${abName} — you're already concentrating`
            : baseReason ? `${abName} — ${baseReason}` : abName;
    const chargePill = opts.showChargePill ? actionBarChargePillModel(ability, caster, spellType ?? '', source ?? '') : null;
    const buttonTitle = abTitle + (chargePill ? ` — ${escapeHtml(chargePill.label)}` : '');
    const titleAttr = `data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${buttonTitle}"${opts.showChargePill ? ` aria-label="${buttonTitle}"` : ''}`;

    const buttonHtml = `
        <button
            class="btn btn-sm btn-${btnVariant} btn_ability_ranges${runeBtnClass}${extraClass}"
            ${actionAttr}${disabledAttr}
            data-ft-charge-hint="1"
            ${opts.showChargePill ? 'data-ft-show-charge-pill="1"' : ''}
            data-ft-caster-id="${escapeHtml(String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_creature_id') || ftItemsCol(caster, 'item_id') || ''))}"
            data-ft-charge-base-title="${escapeHtml(chargeBaseTitle)}"
            data-ft-charge-base-ring="${otherResourceShort && !concentrationBusy ? '1' : '0'}"
            data-ft-charge-priority-title="${statusRequiredReason || concentrationBusy || priorityReason ? '1' : '0'}"
            data-ft-charge-bar-chooses-amount="${barChoosesAmount ? '1' : '0'}"
            data-ft-free-mana="${castFreeOfMana ? '1' : '0'}"
            ${chargeShort ? 'data-ft-charge-blocked="1" aria-disabled="true" tabindex="-1"' : ''}
            data-ability_id="${escapeHtml(abilityId)}"
            data-item_type="${escapeHtml(itemType)}"
            data-ability_does_not_effect_inventory="${bool('ability_does_not_effect_inventory', true)}"
            data-ability_only_affects_inventory="${lv(ability, 'ability_only_affects_inventory') === '1' ? '1' : '0'}"
            data-effect_only_affects_same_team_type="${bool('effect_only_affects_same_team_type')}"
            data-effect_only_affects_opposing_team_type="${bool('effect_only_affects_opposing_team_type')}"
            data-effect_can_heal_inventory="${bool('effect_can_heal_inventory', true)}"
            data-effect_can_only_heal_inventory="${bool('effect_can_only_heal_inventory', true)}"
            data-ap_required="${escapeHtml(String(apRequired))}"
            data-range="${escapeHtml(range)}"
            data-aoe_type="${escapeHtml(aoeType)}"
            data-aoe_distance="${escapeHtml(lv(ability, 'aoe_distance'))}"
            data-aoe_distance_height="${escapeHtml(lv(ability, 'aoe_distance_height'))}"
            data-aoe_distance_depth="${escapeHtml(lv(ability, 'aoe_distance_depth'))}"
            data-spell_upcast_increase_aoe_ft="${escapeHtml(lv(ability, 'spell_upcast_increase_aoe_ft') || '0')}"
            data-persistent_aoe="${escapeHtml(lv(ability, 'persistent_aoe'))}"
            data-wall_attachment="${['B9032A2F-B68E-0D7E-A234-90CAD8959892', '30DF4F8A-BA72-91D3-74F6-A26FA3A54B9B', 'D7049DF3-2800-E170-B3B3-E5AB57CD7C9F', '3375068E-F4FB-C567-C7D4-D2D4B861CD10', '98E982F2-46EF-1B81-87D2-6B0E8AFDE922', 'E10D3A9E-D524-C1E3-7745-99D69403CE63', 'C88CB529-C244-07ED-92F4-260FCAC62992', 'DA53F59D-2714-47C7-A973-C68516B817D2', 'FC95B5FB-B47D-7848-F956-06663487666C', '2999073F-2DDA-E9D5-64F2-07B7F1E3105F'].includes(String(abilityId).toUpperCase()) ? 'surface' : String(abilityId).toUpperCase() === 'FB72381D-4541-0398-CD0B-9F6A7F997D98' ? 'floating' : ''}"
            data-aoe_follows_target="${escapeHtml(lv(ability, 'aoe_follows_target') || '0')}"
            data-arcing_trajectory="${Number(lv(ability, 'arcing_trajectory')) === 1 ? '1' : '0'}"
            data-max_targets="${escapeHtml(lv(ability, 'max_targets'))}"
            data-spell_level="${escapeHtml(lv(ability, 'spell_level') || '0')}"
            data-menta_level="${escapeHtml(lv(ability, 'menta_level') || '0')}"
            data-spell_casting_time="${escapeHtml(String(lv(ability, 'spell_casting_time') ?? ''))}"
            data-roll_type="${escapeHtml(lv(ability, 'roll_type'))}"
            data-dice="${escapeHtml(lv(ability, 'dice'))}"
            data-die="${escapeHtml(lv(ability, 'die'))}"
            data-damage_type="${escapeHtml(lv(ability, 'damage_type'))}"
            data-ft-aoe-excludes-caster="${excludesAoeCaster ? '1' : '0'}"
            data-spell_type="${escapeHtml(spellType ?? '')}"
            data-input_id_reference="${escapeHtml(inputIdRef)}"
            data-ft-input-family="${inputFamily}"
            ${dataNestedInvAttr}${dataSourceAttr}
            data-caster_in_combat="${casterInCombat}"
            data-caster_team="${escapeHtml(String(casterTeam))}"
            data-caster_aps="${escapeHtml(String(casterAps))}"
            data-caster_reaction="${escapeHtml(String(casterReaction))}"
            data-caster_mana="${escapeHtml(String(casterMana))}"
            data-caster_psi="${escapeHtml(String(casterPsi))}"
            ${titleAttr}
            data-icon_map="${escapeHtml(icon)}"${triggerSnippetAttr}${targetGateAttrs}>
            ${buttonInnerIcon}
            ${renderActionBarChargePill(chargePill)}
        </button>`;

    if (!abilityHasTriggers(ability)) return buttonHtml;

    // 9. trigger wrapper (sparkle border) + active/inactive toggle badge.
    // The sparkle BORDER signals "this ability carries a trigger effect" and shows in BOTH contexts.
    // The toggle BADGE (the ✓/✕ check) is ENCOUNTER-ONLY: a static item-creature statblock
    // (item.php?id=<creature>, NOT placed in an encounter) carries no encounter_creature_id / encounter_id,
    // so toggleTriggerActive() has nothing to write to and the check is meaningless there. So on a static
    // statblock we keep the wrapper + border but OMIT the badge.
    const triggerActive = abilityTriggerIsActive(abilityId, caster);
    const wrapperClass = 'ft-trigger-ability-wrapper' + (triggerActive ? ' ft-trigger-active' : '');

    const ecId = String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_creature_id') ?? '');
    const encId = String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_id') ?? '');
    const inEncounterContext = (typeof checkIfValidUUID === 'function')
        ? (checkIfValidUUID(ecId) && checkIfValidUUID(encId))
        : (ecId !== '' && encId !== '');
    if (!inEncounterContext) return `<div class="${wrapperClass}">${buttonHtml}</div>`;

    const newInactive = triggerActive ? '1' : '0';
    const badgeClass = 'ft-trigger-badge ' + (triggerActive ? 'ft-trigger-badge-active' : 'ft-trigger-badge-inactive');
    const badgeTitle = triggerActive ? 'Auto-trigger active – click to disable' : 'Auto-trigger inactive – click to enable';

    return `<div class="${wrapperClass}">${buttonHtml}<span class="${badgeClass}" title="${escapeHtml(badgeTitle)}" data-ft-abtn-action="toggle-trigger" data-ft-ability-id="${escapeHtml(abilityId)}" data-ft-encounter-creature-id="${escapeHtml(ecId)}" data-ft-encounter-id="${escapeHtml(encId)}" data-ft-inactive="${newInactive}"></span></div>`;
}

// ── render_simple_trigger_ability_button() port ──────────────────────────────
// Trigger sparkle wrapper + active/inactive badge around a hardcoded use-snippet
// button (Resolve, Opportunity Attack, …) whose ability_arr isn't carried as a layer.
// When isDisabled: bare warning + ARIA-disabled (no wrapper), mirroring the PHP.
export interface SimpleTriggerOpts {
    title?: string | null;
    isDisabled?: boolean;
    useSnippet?: string | null;
    extraAttrs?: Record<string, string>;
}
export function renderSimpleTriggerAbilityButton(
    abilityId: string,
    btnClass: string,
    iconHtml: string,
    caster: CasterLike,
    opts: SimpleTriggerOpts = {},
): string {
    const { title = null, isDisabled = false, useSnippet = null, extraAttrs = {} } = opts;
    const titleAttr = title ? ` title="${escapeHtml(title)}"` : '';
    let extraAttrsHtml = '';
    for (const k of Object.keys(extraAttrs)) extraAttrsHtml += ` ${k}="${escapeHtml(String(extraAttrs[k]))}"`;

    if (isDisabled) {
        let cls = btnClass.replace(/\bbtn-(?:info|primary|secondary|success|dark|danger|light|muted)\b/, 'btn-warning');
        if (cls === btnClass) cls = `${btnClass} btn-warning`.trim();
        cls = `${cls} ft-ability-btn-charge-blocked`.trim();
        return `<button class="${escapeHtml(cls)}" aria-disabled="true" tabindex="-1" data-ability_id="${escapeHtml(abilityId)}" data-ft-charge-blocked="1"${extraAttrsHtml}${titleAttr}>${iconHtml}</button>`;
    }

    const snippet = useSnippet ?? `handleAbilityBtnSingleClick('${abilityId}');`;
    const triggerActive = abilityTriggerIsActive(abilityId, caster);
    const wrapperClass = 'ft-trigger-ability-wrapper' + (triggerActive ? ' ft-trigger-active' : '');
    const triggerBtn = `<button class="${escapeHtml(btnClass)}" data-ft-abtn-action="trigger-primary" data-ability_id="${escapeHtml(abilityId)}" data-ft-trigger-use-snippet="${escapeHtml(snippet)}"${extraAttrsHtml}${titleAttr}>${iconHtml}</button>`;

    const ecId = String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_creature_id') ?? '');
    const encId = String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_id') ?? '');
    // Same split as generateAbilityButton: KEEP the sparkle border (signals a trigger effect) in both
    // contexts, but the ✓/✕ toggle badge is ENCOUNTER-ONLY — no encounter_creature/encounter id ⇒ nothing
    // to write, so a static item-creature statblock keeps the wrapper + border but omits the badge.
    const inEncounterContext = (typeof checkIfValidUUID === 'function')
        ? (checkIfValidUUID(ecId) && checkIfValidUUID(encId))
        : (ecId !== '' && encId !== '');
    if (!inEncounterContext) return `<div class="${wrapperClass}">${triggerBtn}</div>`;

    const newInactive = triggerActive ? '1' : '0';
    const badgeClass = 'ft-trigger-badge ' + (triggerActive ? 'ft-trigger-badge-active' : 'ft-trigger-badge-inactive');
    const badgeTitle = triggerActive ? 'Auto-trigger active – click to disable' : 'Auto-trigger inactive – click to enable';
    return `<div class="${wrapperClass}">${triggerBtn}<span class="${badgeClass}" title="${escapeHtml(badgeTitle)}" data-ft-abtn-action="toggle-trigger" data-ft-ability-id="${escapeHtml(abilityId)}" data-ft-encounter-creature-id="${escapeHtml(ecId)}" data-ft-encounter-id="${escapeHtml(encId)}" data-ft-inactive="${newInactive}"></span></div>`;
}

export interface LegacyAbilityButtonInput {
    ability_id: string;
    caster_id: string;
    options: AbilityButtonOpts & { listControls?: boolean };
}

/** Native list mounts supply identity/options; all mutable reads come from this caster. */
export function renderLegacyAbilityButton(input: LegacyAbilityButtonInput, caster: CachedCaster): string {
    const options = input.options ?? {};
    const ability = resolveAbilityNest(input.ability_id, caster);
    if (!recordOf(ability)?.item_layer_types) throw new Error('unported:ability-definition:not-held:' + input.ability_id);
    let html = generateAbilityButton(ability, caster, options);
    if (options.listControls && options.spellType === 'innate_spell_per_long_rest') {
        const entry = ftLayerEntries(caster, options.spellType).find(row => abilityEntryId(row).toUpperCase() === input.ability_id.toUpperCase());
        const id = `${input.ability_id}_innate_spell_per_long_rest_charges`;
        html += chargeInput(id, 0, abilityEntryMaxCharges(entry, 1), abilityEntryCharges(entry, 0), !ftHasPlayerPermission(caster),
            `changeCharges('${input.ability_id}','${id}');`);
    }
    return html;
}

// ── caster_can_use_opportunity_attack() port ─────────────────────────────────
// True if any equipped (main/off/both) weapon is a MELEE weapon_type, or the caster
// has any natural_weapon (treated as melee). Gates the OA button enabled/disabled.
const MELEE_WEAPON_TYPE_ID = '1364260D-E37C-4257-AD16-5E745107811A';
const OA_SLOT_MAIN = 'CEF334BF-DE96-1BEA-F72A-A4C98FBF36AD';
const OA_SLOT_OFF = '4FF85144-67EC-9D87-AE28-15137A1A2481';
const OA_SLOT_BOTH = 'E6D02ACB-D3E9-DD8B-762A-31FE8C8D898D';
export function casterCanUseOpportunityAttack(caster: CasterLike): boolean {
    if (ftLayerHas(caster, 'inventory')) {
        for (const inv of ftLayerEntries(caster, 'inventory')) {
            const slot = readInventoryEquippedToken(inv, caster);
            if (slot !== OA_SLOT_MAIN && slot !== OA_SLOT_OFF && slot !== OA_SLOT_BOTH) continue;
            if (String(ftLayerValue(inv, 'weapon_type') ?? '') === MELEE_WEAPON_TYPE_ID) return true;
        }
    }
    // A PC caster's natural weapon (monk unarmed / free-hand slam) lives ONLY at
    // computed.creature.natural_weapon; the JS layer walk doesn't bridge computed clusters
    // ([[feedback_ftlayervalue_no_computed_bridge]]), so ftLayerHas('natural_weapon') misses it and
    // the OA button would render disabled for an unarmed monk. Merge the cluster the same way
    // renderNaturalWeapons() / the list-display card do (mirrors PHP creature_natural_weapon_entries).
    const nwLayer = ftLayerHas(caster, 'natural_weapon') ? ftLayerEntries(caster, 'natural_weapon') : [];
    if (Array.isArray(nwLayer) && nwLayer.length > 0) return true;
    const nwCluster = caster.computed?.creature?.natural_weapon;
    if (Array.isArray(nwCluster)) return nwCluster.length > 0;
    return nwCluster !== null && nwCluster !== undefined && nwCluster !== '';
}
